import { useState, useEffect, useRef } from 'react';
import { Box, Button, Stack, Accordion, AccordionSummary, AccordionDetails } from '@mui/joy';
import LoginPage from './LoginPage';
import { CaretDown } from "@phosphor-icons/react";
import './LandingPage.css';

// ─── DATA ────────────────────────────────────────────────────────────────────
const PAINS = [
    { title: 'Хаос в материале', body: 'First Aid, Pathoma, Sketchy, UWorld. Всё по чуть-чуть, ничего до конца.' },
    { title: 'Языковой барьер', body: 'Объяснения длинные и сухие. Теряешь время на перевод вместо понимания клиники.' },
    { title: 'Нет системы', body: 'Непонятно что учить, в каком порядке и сколько. Каждый день начинаешь с нуля.' },
    { title: 'Разрозненные ресурсы', body: 'Тесты в одном месте, теория в другом, планы в третьем. В итоге больше трат и потерянного времени.' },
    { title: 'Нет ощущения прогресса', body: 'Занимаешься неделями, но непонятно насколько ты готов.' },
    { title: 'Непонятный формат', body: 'Клинические тесты, двухшаговые вопросы. Формат USMLE сам по себе вызов.' },
];

const FEATURES = [
    { title: '5 000+ вопросов', body: 'Мини-тесты по любой дисциплине. После каждого ответа развернутое объяснение с патофизиологией.' },
    { title: 'Клинические тесты', body: 'Полноценные случаи в формате USMLE: история, физикальное обследование, лабораторные данные.' },
    { title: '100+ видеолекций', body: 'Записанные лекции по всем ключевым дисциплинам. Короткий формат, понятный язык, без воды. Смотри в своём темпе.' },
    { title: 'Study Plans', body: 'Готовые учебные планы по неделям. Система ведет тебя: что читать, что тестировать, в каком порядке.' },
    { title: '1 280 текстовых модулей', body: '16 дисциплин First Aid. Теория рядом с тестами. Не нужно переключаться между ресурсами.' },
    { title: 'Аналитика прогресса', body: 'Точность по каждой категории, слабые темы, серия активности и динамика за неделю.' },
];

const COMPARE_ROWS = [
    { feature: 'Подготовка на русском языке',          apex: true,  uworld: false, amboss: false, unis: true  },
    { feature: 'USMLE-формат клинических вопросов',    apex: true,  uworld: true,  amboss: true,  unis: false },
    { feature: 'Готовый план подготовки к Step 1',     apex: true,  uworld: false, amboss: null,  unis: false },
    { feature: 'Глубокие объяснения патофизиологии',   apex: true,  uworld: true,  amboss: true,  unis: false },
    { feature: 'Библиотека на ENG + RUS',               apex: true,  uworld: false, amboss: true,  unis: false },
    { feature: 'Видеолекции',                          apex: true,  uworld: false, amboss: false, unis: '3-часовые лекции' },
    { feature: 'Качественные презентации',             apex: true,  uworld: false, amboss: false, unis: 'Скриншоты книг' },
    { feature: 'Авторы с опытом Match',                apex: true,  uworld: null,  amboss: null,  unis: 'Нет опыта резидентуры' },
    { feature: 'Полноценный банк вопросов',            apex: true,  uworld: true,  amboss: true,  unis: 'Пиратские PDF' },
    { feature: 'Отслеживание прогресса подготовки',    apex: true,  uworld: true,  amboss: true,  unis: false },
    { feature: 'Поддержка студентов',                  apex: true,  uworld: false, amboss: false, unis: null  },
];

const STEPS = [
    { title: 'Зарегистрируйся и пройди диагностику', body: 'Создай аккаунт, пройди короткий диагностический тест и получи готовый план подготовки.' },
    { title: 'Узнай свои слабые места', body: '20 вопросов за 15 минут. Система определит пробелы и покажет, на чём ты теряешь баллы прямо сейчас.' },
    { title: 'Готовься каждый день по системе', body: 'Каждый день целевые тесты по твоим слабым темам, теория и аналитика прогресса. Всё автоматически.' },
];

const FAQS = [
    { q: 'Чем APEX отличается от UWorld?', a: 'UWorld - в середине и перед экзаменом. APEX - для системного изучения материала с нуля до уверенного уровня. В APEX: объяснения на русском экономят время (не нужно переводить каждый термин), текстовые модули структурируют знания, Study Plans показывают что учить дальше. UWorld даёт только вопросы, а APEX даёт систему.' },
    { q: 'Нужен ли UWorld, если я использую APEX?', a: 'Зависит от твоего уровня. Если начинаешь с нуля или начал, но запутался - APEX для системной подготовки. UWorld потребуется потом, после изучения теории и закрепления материала на APEX.' },
    { q: 'Контент на русском или английском?', a: 'Вопросы и текстовая библиотека на двух языках - английский и русский. Вопросы на английском - как на реальном экзамене. Объяснения на русском - экономишь 30-50% времени, не переводя каждый термин. Модули на русском с ключевыми терминами на английском - запоминаешь медицинскую лексику естественно.' },
    { q: 'Что такое Study Plans?', a: 'Планы подготовки на основе диагностики. Система анализирует твои слабые зоны и строит маршрут: какие модули читать, какие тесты проходить, в каком порядке. Ты просто открываешь платформу и видишь задачи на сегодня - без планирования и хаоса.' },
    { q: 'Можно попробовать бесплатно?', a: '5 дней бесплатного доступа. Никакой карты не нужно. Просто зарегистрируйся и начни.' },
];

const STATS = [
    { val: '5 000+', label: 'вопросов' },
    { val: '100+', label: 'видеолекций' },
    { val: '1 280', label: 'модулей' },
    { val: '16', label: 'дисциплин' },
];

const AUTHOR_STATS = [
    { label: 'Step 1', val: '239' },
    { label: 'Step 2 CK', val: '260' },
    { label: 'Match', val: 'Internal Medicine' },
    { label: 'ECFMG', val: 'Certified' },
];


// ─── HELPERS ─────────────────────────────────────────────────────────────────
function CheckMark({ ok }) {
    if (typeof ok === 'string') return <span className="lp-check-text">{ok}</span>;
    if (ok === true)  return <span className="lp-check lp-check--yes" role="img" aria-label="Да">&#10003;</span>;
    if (ok === false) return <span className="lp-check lp-check--no" role="img" aria-label="Нет">&#10007;</span>;
    return <span className="lp-check lp-check--partial" role="img" aria-label="Частично">&#9675;</span>;
}

function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    el.classList.add('lp-visible');
                    observer.unobserve(el);
                }
            },
            { threshold: 0.12 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return ref;
}

function Section({ children, bg = 'cream', label, title, desc, className = '', ...props }) {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            className={`lp-section lp-section--${bg} lp-reveal ${className}`}
            {...props}
        >
            <div className="lp-section__inner">
                {label && <p className="lp-section__label">{label}</p>}
                {title && <h2 className="lp-section__title">{title}</h2>}
                {desc && <p className="lp-section__desc">{desc}</p>}
                {children}
            </div>
        </section>
    );
}

// ─── BUTTON STYLES ───────────────────────────────────────────────────────────
const btnBase = { fontFamily: 'var(--lp-sans)', '--Button-radius': '6px', borderRadius: '6px !important', py: 1.5, px: 4, fontSize: 15, border: 'none', boxShadow: 'none', transition: 'background 0.15s ease' };
const btnTerra = { ...btnBase, fontWeight: 700, fontSize: 15, bgcolor: 'var(--lp-terra)', color: '#fff', px: 4, '&:hover': { bgcolor: 'var(--lp-terra-hover)', boxShadow: 'none' } };
const btnDark = { ...btnBase, fontWeight: 600, fontSize: 14, bgcolor: 'var(--lp-charcoal)', color: '#fff', px: 3.5, '&:hover': { bgcolor: '#1f1e1b', boxShadow: 'none' } };
const btnGhost = { ...btnBase, fontWeight: 500, fontSize: 15, borderColor: 'var(--lp-border-dark)', color: 'var(--lp-text-on-dark)', px: 4, border: '1px solid var(--lp-border-dark)', '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.25)' } };

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────
export default function LandingPage() {
    const [showAuth, setShowAuth] = useState(false);
    const [authTab, setAuthTab] = useState(1);

    const openRegister = () => { setAuthTab(1); setShowAuth(true); };
    const openLogin    = () => { setAuthTab(0); setShowAuth(true); };

    if (showAuth) {
        return <LoginPage defaultTab={authTab} onBack={() => setShowAuth(false)} />;
    }

    return (
        <Box className="lp">
            {/* Skip link */}
            <a href="#main-content" className="lp-skip">
                Перейти к содержимому
            </a>

            {/* ── NAV ──────────────────────────────────────────── */}
            <nav className="lp-nav" aria-label="Главная навигация">
                <span className="lp-nav__logo">APEX MED</span>
                <div style={{ position: 'relative', marginRight: 'auto', marginLeft: '48px' }}
                     onMouseEnter={e => e.currentTarget.querySelector('.lp-products-dropdown').style.display = 'block'}
                     onMouseLeave={e => e.currentTarget.querySelector('.lp-products-dropdown').style.display = 'none'}>
                    <span style={{ fontFamily: 'var(--lp-sans)', fontWeight: 700, fontSize: '0.9rem', color: 'var(--lp-text-2)', transition: 'color 0.2s', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        Продукты <CaretDown size={12} />
                    </span>
                    <div className="lp-products-dropdown" style={{ display: 'none', position: 'absolute', top: '100%', left: 0, paddingTop: '8px', background: 'transparent', minWidth: '180px', zIndex: 100 }}>
                      <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid rgba(0,0,0,0.06)', padding: '6px' }}>
                        {[{ label: 'APEX USMLE PRO', href: '/pro' }, { label: 'FANKI', href: '/fanki' }].map(p => (
                            <a key={p.href} href={p.href} style={{ display: 'block', padding: '10px 14px', borderRadius: '8px', fontFamily: 'var(--lp-sans)', fontWeight: 600, fontSize: '0.85rem', color: 'var(--lp-text)', textDecoration: 'none', transition: 'background 0.15s' }}
                               onMouseEnter={e => e.target.style.background = 'rgba(0,0,0,0.04)'}
                               onMouseLeave={e => e.target.style.background = 'transparent'}>
                                {p.label}
                            </a>
                        ))}
                      </div>
                    </div>
                </div>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button variant="plain" color="neutral" onClick={openLogin}
                        sx={{ fontFamily: 'var(--lp-sans)', fontWeight: 500, color: 'var(--lp-text-2)', minHeight: 44, px: 2, '&:hover': { color: 'var(--lp-text)', bgcolor: 'transparent' } }}>
                        Войти
                    </Button>
                    <Button onClick={openRegister} sx={btnDark}>
                        Начать бесплатно
                    </Button>
                </Stack>
            </nav>

            {/* ── MAIN CONTENT ─────────────────────────────────── */}
            <main id="main-content">

                {/* ── HERO ─────────────────────────────────────── */}
                <section className="lp-hero" aria-label="Введение">
                    <div className="lp-hero__inner">
                        <h1 className="lp-hero__title">
                            Готовься к&nbsp;USMLE<br />
                            <span className="lp-hero__accent">как профессионал</span>
                        </h1>
                        <hr className="lp-hero__rule" aria-hidden="true" />
                        <p className="lp-hero__subtitle">
                            5 000+ вопросов, 100+ видеолекций, учебные планы
                            и&nbsp;текстовые модули на&nbsp;русском. Всё для подготовки
                            к&nbsp;USMLE в&nbsp;одном месте.
                        </p>
                        <div className="lp-hero__actions">
                            <Button onClick={openRegister} sx={btnTerra}>
                                Начать подготовку
                            </Button>
                            <Button variant="outlined" onClick={openLogin} sx={btnGhost}>
                                Войти в аккаунт
                            </Button>
                        </div>
                    </div>
                    <div className="lp-hero-rule" aria-hidden="true" />
                </section>

                {/* ── STATS STRIP ──────────────────────────────── */}
                <div className="lp-stats-strip" role="region" aria-label="Ключевые цифры">
                    <div className="lp-stats-strip__inner">
                        {STATS.map((s, i) => (
                            <div key={i} className="lp-stats-strip__item">
                                <span className="lp-stats-strip__val">{s.val}</span>
                                <span className="lp-stats-strip__label">{s.label}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── PAIN POINTS ──────────────────────────────── */}
                <Section bg="fog" label="Проблема" title="Узнаёшь себя?"
                    desc="Большинство студентов из СНГ сталкиваются с одними и теми же проблемами."
                    aria-label="Проблемы подготовки">
                    <div className="lp-pains">
                        {PAINS.map((p, i) => (
                            <div key={i} className="lp-pain">
                                <div className="lp-pain__num">{String(i + 1).padStart(2, '0')}</div>
                                <h3 className="lp-pain__title">{p.title}</h3>
                                <p className="lp-pain__body">{p.body}</p>
                            </div>
                        ))}
                    </div>
                </Section>

                {/* ── FEATURES ─────────────────────────────────── */}
                <Section bg="cream" label="Что внутри" title={<>Один инструмент.<br />Всё что нужно.</>}
                    desc="Не нужно собирать ресурсы по кусочкам. APEX это полноценная среда подготовки."
                    aria-label="Возможности платформы">
                    <ul className="lp-features">
                        {FEATURES.map((f, i) => (
                            <li key={i} className="lp-feature">
                                <span className="lp-feature__num">{String(i + 1).padStart(2, '0')}</span>
                                <div className="lp-feature__content">
                                    <h3 className="lp-feature__title">{f.title}</h3>
                                    <p className="lp-feature__body">{f.body}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Section>

                {/* ── COMPARISON ────────────────────────────────── */}
                <Section bg="dark" label="Сравнение"
                    title={<>APEX - стартовая система<br />подготовки для IMG</>}
                    desc="Что действительно важно при подготовке к Step 1."
                    aria-label="Сравнение с конкурентами">
                    <div className="lp-compare-wrap" role="region" aria-label="Сравнительная таблица" tabIndex={0}>
                        <div className="lp-compare-scroll">
                            <table className="lp-compare">
                                <caption className="lp-sr-only">
                                    Сравнение APEX MED с UWorld, AMBOSS и другими курсами
                                </caption>
                                <thead>
                                    <tr>
                                        <th scope="col">Что получает студент</th>
                                        <th scope="col" className="lp-compare__apex-head">
                                            <span className="lp-compare__apex-badge">Обучающая платформа</span>
                                            APEX
                                        </th>
                                        <th scope="col">UWorld</th>
                                        <th scope="col">AMBOSS</th>
                                        <th scope="col">Другие курсы</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="lp-compare__type-row">
                                        <td>Тип платформы</td>
                                        <td className="lp-compare__apex-col">
                                            <span className="lp-compare__type-label lp-compare__type-label--apex">Обучающая платформа для подготовки к USMLE Step 1</span>
                                        </td>
                                        <td><span className="lp-compare__type-label">Банк вопросов</span></td>
                                        <td><span className="lp-compare__type-label">Банк вопросов + мед. библиотека</span></td>
                                        <td><span className="lp-compare__type-label">Курсы лекций</span></td>
                                    </tr>
                                    {COMPARE_ROWS.map((row, i) => (
                                        <tr key={i}>
                                            <td>{row.feature}</td>
                                            <td className="lp-compare__apex-col"><CheckMark ok={row.apex} /></td>
                                            <td><CheckMark ok={row.uworld} /></td>
                                            <td><CheckMark ok={row.amboss} /></td>
                                            <td><CheckMark ok={row.unis} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Section>

                {/* ── STEPS ────────────────────────────────────── */}
                <Section bg="fog" label="Как начать" title="Начни готовиться к USMLE за 5 минут"
                    desc="От регистрации до первого теста. Три простых шага."
                    aria-label="Как начать">
                    <div className="lp-steps">
                        {STEPS.map((s, i) => (
                            <div key={i} className="lp-step">
                                <div className="lp-step__num">{String(i + 1).padStart(2, '0')}</div>
                                <h3 className="lp-step__title">{s.title}</h3>
                                <p className="lp-step__body">{s.body}</p>
                            </div>
                        ))}
                    </div>
                    <Box sx={{ mt: 5 }}>
                        <Button onClick={openRegister} sx={btnDark}>
                            Начать подготовку
                        </Button>
                    </Box>
                </Section>

                {/* ── AUTHOR ───────────────────────────────────── */}
                <Section bg="cream" label="Об авторе"
                    aria-label="Об авторе">
                    <div className="lp-author">
                        <div className="lp-author__card">
                            <div className="lp-author__avatar" role="img" aria-label="Автор APEX MED">
                                <span aria-hidden="true" style={{ fontSize: 24 }}>&#9877;</span>
                            </div>
                            <p className="lp-author__name">Автор APEX MED</p>
                            <p className="lp-author__role">Internal Medicine, ECFMG Certified</p>
                            <div className="lp-author__stats">
                                {AUTHOR_STATS.map(s => (
                                    <div key={s.label} className="lp-author__stat">
                                        <span className="lp-author__stat-label">{s.label}</span>
                                        <span className="lp-author__stat-val">{s.val}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="lp-author__text">
                            <h2 className="lp-author__heading">
                                Я прошёл этот путь сам
                            </h2>
                            <p className="lp-author__paragraph">
                                Готовясь к USMLE, я понял, что русскоязычным студентам не хватает
                                нормального ресурса для подготовки. Всё, что есть - либо дорого,
                                либо разрозненно, либо без системы.
                            </p>
                            <p className="lp-author__paragraph">
                                Я сдал экзамены и прошёл матч, но этот путь можно было пройти
                                быстрее и эффективнее, если бы всё необходимое было в одном месте.
                            </p>
                            <p className="lp-author__paragraph">
                                Поэтому я создал APEX - полноценную систему подготовки,
                                которая не даст заблудиться:
                            </p>
                            <ul className="lp-author__list">
                                <li>Мини-тесты для закрепления материала</li>
                                <li>Клинические задачи в стиле NBME и UWorld</li>
                                <li>Авторские видеолекции на русском</li>
                                <li>Библиотека с объяснениями на русском и английском</li>
                                <li>Персональная статистика и аналитика</li>
                            </ul>
                            <p className="lp-author__paragraph">
                                Вопросы закрепляют факты. Объяснения - без воды.
                                Планы закрывают пробелы, а не гонят по всему материалу.
                            </p>
                            <p className="lp-author__paragraph lp-author__highlight">
                                APEX - это не перевод UWorld. Это система-путеводитель, которая
                                объединяет лучшее из американских ресурсов с поддержкой на русском
                                языке там, где это экономит время, и на английском там, где это
                                помогает запомнить термины.
                            </p>
                        </div>
                    </div>
                </Section>

                {/* ── FAQ ──────────────────────────────────────── */}
                <Section bg="fog" label="FAQ" title="Частые вопросы"
                    aria-label="Частые вопросы" className="lp-section--faq">
                    {FAQS.map((faq, i) => (
                            <Accordion key={i} sx={{
                                bgcolor: 'transparent',
                                borderTop: '1px solid var(--lp-border)',
                                '&:last-of-type': { borderBottom: '1px solid var(--lp-border)' },
                                '&::before': { display: 'none' },
                            }}>
                                <AccordionSummary
                                    indicator={<CaretDown size={18} />}
                                    sx={{
                                        py: 2.5, px: 0,
                                        fontFamily: 'var(--lp-sans)',
                                        fontWeight: 600, fontSize: 15,
                                        color: 'var(--lp-charcoal)',
                                        '&:hover': { bgcolor: 'transparent', color: 'var(--lp-terra)' },
                                        transition: 'color 0.15s',
                                    }}
                                >
                                    {faq.q}
                                </AccordionSummary>
                                <AccordionDetails sx={{ px: 0, pb: 2.5 }}>
                                    <p className="lp-faq__answer">{faq.a}</p>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                </Section>

            </main>

            {/* ── FINAL CTA ────────────────────────────────── */}
            <footer className="lp-cta" role="contentinfo">
                <div className="lp-cta__inner">
                    <h2 className="lp-cta__title">Начни прямо сейчас</h2>
                    <p className="lp-cta__desc">
                        5 дней полного доступа. Бесплатно.
                        Просто зайди и посмотри сам.
                    </p>
                    <Button onClick={openRegister} sx={{ ...btnTerra, fontSize: 16, px: 4.5 }}>
                        Начать подготовку
                    </Button>
                    <p className="lp-cta__fine">
                        Отмена в любой момент &middot; apexqbank.ru
                    </p>
                </div>
            </footer>

            {/* ── LEGAL FOOTER ───────────────────────────────── */}
            <div className="lp-legal">
                <div className="lp-legal__inner">
                    <div className="lp-legal__brand">APEX <span className="lp-legal__brand-sep">|</span> АПЕКС</div>
                    <div className="lp-legal__divider" />
                    <div className="lp-legal__grid">
                        <div className="lp-legal__col">
                            <a href="https://apexmeded.ru/privacypolicy" className="lp-legal__link" target="_blank" rel="noopener noreferrer">Политика конфиденциальности</a>
                            <a href="https://apexmeded.ru/legal" className="lp-legal__link" target="_blank" rel="noopener noreferrer">Публичная оферта</a>
                            <a href="/consent" className="lp-legal__link">Согласие на получение информационных и рекламных материалов</a>
                        </div>
                        <div className="lp-legal__col">
                            <p className="lp-legal__text">ИП Ашуров Мухаммаджон Фуркатович</p>
                            <p className="lp-legal__text">ИНН 772996111132</p>
                            <p className="lp-legal__text lp-legal__text--mt">ОГРНИП 325774600521362</p>
                            <p className="lp-legal__text">от 12.08.2025</p>
                        </div>
                        <div className="lp-legal__col">
                            <p className="lp-legal__text">Эл. адрес:</p>
                            <a href="mailto:ashurov.mukhammad@yandex.ru" className="lp-legal__link">ashurov.mukhammad@yandex.ru</a>
                            <p className="lp-legal__text lp-legal__text--mt">Телеграм: <a href="https://t.me/md_ashurov" className="lp-legal__link" target="_blank" rel="noopener noreferrer">@md_ashurov</a></p>
                        </div>
                    </div>
                </div>
            </div>
        </Box>
    );
}
